import * as React from 'react'
import { Button, StatusBadge, Well } from '@pushly/aqe/lib/components'
import { observer } from 'mobx-react'
import { DollarOutlined, DownloadOutlined, Loading3QuartersOutlined } from '@ant-design/icons'
import { Table, Input } from 'antd'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { AccountService } from '@halo/services/account.service'
import { AccountDto } from '@halo/dtos/account.dto'
import { ColumnProps } from 'antd/es/table'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { CompoundTable } from '@halo/components/compound-table/compound-table'
import moment from 'moment-timezone'
import { numberAsCurrency } from '@halo/_utils/numeral'
import { BillingRecordResponse } from '@pushly/models/lib/structs/billing'
import { SUPPORTED_BILLING_CURRENCIES } from '@halo/constants'
import { classnames } from '@halo/_utils/classnames'
import './style/account-billing-history-well.scss'
import { AccountChargeDetails } from '@halo/components/account-charge-form/account-charge-details'
import * as randomstring from 'randomstring'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

class RecordTableDataDto {
    public static fromAccountDto(fields: any): RecordTableDataDto {
        const dto = new RecordTableDataDto()
        Object.assign(dto, fields, {
            key: fields.id,
        })
        return dto
    }

    public readonly key: number
}

interface IColumnProps extends ColumnProps<RecordTableDataDto & any> {
    dataKey?: string
}

interface IAccounts {
    loading: boolean
    account: AccountDto
}

interface IAccountsState {
    loading: boolean
    recordsLoaded: boolean
    records: any[]
    recordsMeta: any
    sortedInfo: any
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks

    activeRecord: BillingRecordResponse | false
    drawerKey?: string
}

@observer
export class AccountBillingHistoryWell extends React.Component<IAccounts, IAccountsState> {
    protected appService: AppService
    protected accountService: AccountService

    public constructor(props: any) {
        super(props)

        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)

        this.state = {
            page: 1,
            pageSize: 15,
            loading: true,
            recordsLoaded: false,
            records: [],
            recordsMeta: {},
            sortedInfo: {
                order: 'ascend',
                columnKey: 'name',
            },

            activeRecord: false,
        }
    }

    public componentDidMount(): void {
        this.ensureRecordsForList()
    }

    public async ensureRecordsForList() {
        await this.fetchRecords()
    }

    public render() {
        let { sortedInfo } = this.state
        sortedInfo = sortedInfo || {}

        const buildColumn = (config: IColumnProps): React.ReactNode => {
            const title = (config.title as string) || titleCase(config.dataKey || (config.key as string))
            if (config.sorter) {
                config.sortOrder = sortedInfo.columnKey === (config.dataKey || config.key) && sortedInfo.order
            }

            return (
                <Table.Column
                    {...config}
                    title={title}
                    dataIndex={config.dataKey?.split('.')}
                    key={config.dataKey || config.key}
                    className={config.className || config.dataKey || (config.key as string)}
                />
            )
        }

        return (
            <>
                <Well
                    className={classnames('account-billing-history', 'type-2')}
                    title="Billing History"
                    hideFooter={true}
                    actions={
                        <>
                            <Input.Search
                                size="small"
                                className="accounts-search"
                                placeholder="Search"
                                enterButton={true}
                                onSearch={this.searchAccounts}
                            />
                        </>
                    }
                >
                    <CompoundTable>
                        <Table
                            size="small"
                            bordered={false}
                            dataSource={this.state.records}
                            onChange={this.handleChange}
                            pagination={false}
                            footer={(currentTableData) => (
                                <SwSimpleLinkPagination
                                    currentTableData={currentTableData}
                                    links={this.state.paginationLinks}
                                    onPrev={(_ev, _link, config) => {
                                        this.setState({ page: config.current! }, () => this.fetchRecords())
                                    }}
                                    onNext={(_ev, _link, config) => {
                                        this.setState({ page: config.current! }, () => this.fetchRecords())
                                    }}
                                />
                            )}
                            loading={
                                this.isLoading && {
                                    indicator: <Loading3QuartersOutlined spin={true} />,
                                }
                            }
                            locale={{
                                emptyText: (
                                    <AntdTableEmptyPlaceholder text="No Previous Charges" icon={<DollarOutlined />} />
                                ),
                            }}
                        >
                            {buildColumn({
                                dataKey: 'created_at',
                                title: 'Date',
                                render: (_, record) => {
                                    return moment.tz(record.created_at, 'America/Chicago').format('LL')
                                },
                            })}
                            {buildColumn({
                                className: 'amount',
                                dataKey: 'metadata.amount',
                                title: 'Amount',
                                render: (_, record) => {
                                    const symbol = SUPPORTED_BILLING_CURRENCIES.find(
                                        (cur) => cur.value === record.metadata.currency,
                                    )!.symbol
                                    return `${symbol}${numberAsCurrency(record.metadata.amount, 2)}`
                                },
                            })}
                            {buildColumn({
                                className: 'line_item_description',
                                dataKey: 'metadata.description',
                                title: 'Description',
                            })}
                            {buildColumn({
                                dataKey: 'stripe_charge_status',
                                title: 'Status',
                                render: this.renderRowStatus,
                            })}
                            {buildColumn({
                                dataKey: 'created_by_user_name',
                                title: 'Created By',
                            })}
                            {buildColumn({
                                key: 'actions',
                                title: ' ',
                                render: this.renderRowActions,
                            })}
                        </Table>
                    </CompoundTable>
                </Well>

                <AccountChargeDetails
                    key={this.state.drawerKey}
                    visible={!!this.state.activeRecord}
                    account={this.props.account}
                    record={this.state.activeRecord}
                    onClose={this.handleDrawerClose}
                />
            </>
        )
    }

    protected get isLoading(): boolean {
        return this.props.loading || (this.state.loading && !this.state.recordsLoaded)
    }

    protected renderRowActions = (record: any): React.ReactNode => {
        let actions: any

        if (record.stripe_charge_receipt_url) {
            actions = [
                {
                    text: 'Receipt',
                    icon: <DownloadOutlined />,
                    altHref: record.stripe_charge_receipt_url,
                    onClick: () => this.handleReceiptDownloadClick(record),
                },
            ]
        }

        return (
            <Button onClick={() => this.handlePrimaryActionClick(record)} size="small" actions={actions}>
                <span>Details</span>
            </Button>
        )
    }

    protected renderRowStatus = (_: any, record: any) => {
        let status = record.stripe_charge_status || record.stripe_invoice_status || 'PENDING'

        if (!record.confimed && !record.processed) {
            status = 'UNCONFIRMED'
        }

        if (record.stripe_charge_refunded) {
            status = 'REFUNDED'
        }

        return (
            <StatusBadge
                expanded={true}
                type="billing"
                status={status}
                hideTooltip={!record.metadata.stripe_error?.message}
                tooltip={record.metadata.stripe_error?.message}
            />
        )
    }

    protected handleChange = (pagination: any, filters: any, sorter: any) => {
        this.setState({
            sortedInfo: sorter,
        })
    }

    protected handlePrimaryActionClick(record: any): void {
        return this.setState({ activeRecord: record, drawerKey: randomstring.generate() })
    }

    protected handleReceiptDownloadClick(record: any): void {
        window.open(record.stripe_charge_receipt_url)
    }

    protected searchAccounts = async (data: string | any) => {
        const value = typeof data !== 'string' ? data.target.value : data

        if (!value || value.length === 0 || value.length >= 2) {
            await this.setState({ page: 1, query: value })
            await this.fetchRecords(value)
        }
    }

    protected handleDrawerClose = async () => {
        return this.setState({ activeRecord: false, drawerKey: undefined })
    }

    protected async fetchRecords(_search?: any) {
        if (!this.props.account) {
            setTimeout(() => this.fetchRecords(), 10)
            return
        }

        this.setState({ loading: true, recordsLoaded: false })
        let update: Partial<IAccountsState> = {
            recordsLoaded: true,
        }

        const {
            ok,
            data: records,
            meta,
        } = await this.accountService.fetchBillingRecordsByAccountId(this.props.account.id, {}, false)

        if (ok) {
            update.records = records
            update.recordsMeta = meta
            update.paginationLinks = meta.links ?? this.state.paginationLinks
        }

        this.setState({
            loading: false,
            ...(update as any),
        })
    }
}
